// API client del módulo Activos + Rentas.
// Backend: /api/v1/admin/assets/* (mcp_django, auth Bearer JWT del usuario).
// El backend filtra por org_id del JWT → cada org ve solo su inventario.

import { ensureFreshToken } from '@/lib/tokenRefresh';
import type { Asset, AssetInput, Movement, Rental } from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/assets';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function req<T = any>(path: string, init?: RequestInit): Promise<T> {
  const jwt = await getJwt();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${jwt}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = (err as any).detail || `Error ${res.status}: ${res.statusText}`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json();
}

// ── Inventario ──────────────────────────────────────────────
export function listAssets(params: {
  q?: string; status?: string; category?: string; limit?: number;
} = {}): Promise<Asset[]> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.status) qs.set('status', params.status);
  if (params.category) qs.set('category', params.category);
  qs.set('limit', String(params.limit ?? 500));
  return req<Asset[]>(`${BASE}/assets?${qs.toString()}`);
}

export function createAsset(payload: AssetInput): Promise<Asset> {
  return req<Asset>(`${BASE}/assets`, { method: 'POST', body: JSON.stringify(payload) });
}

export function updateAsset(id: string, payload: Partial<AssetInput>): Promise<Asset> {
  return req<Asset>(`${BASE}/assets/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
}

export function assetHistory(id: string): Promise<Movement[]> {
  return req<Movement[]>(`${BASE}/assets/${id}/history`);
}

export function holders(): Promise<Asset[]> {
  return req<Asset[]>(`${BASE}/holders`);
}

// ── Rentas ──────────────────────────────────────────────────
/**
 * Crea una renta. `assets[].rate_amount` es el PRECIO NEGOCIADO de esa línea.
 *
 * Importa capturarlo: rentalmaq no tiene lista de precios fija (se negocia por
 * trato), y las 11 rentas migradas quedaron sin tarifa — por eso el BI de flota
 * reporta ingreso $0 aunque haya equipo afuera. Sin rate_amount no hay nada que
 * agregar.
 *
 * `status`: 'quote' | 'reserved' | 'active'. Ojo con 'active': el backend
 * aplica la regla dura de F4 y RECHAZA la salida si el cliente trae expediente
 * incompleto o saldo vencido (cotizar/reservar sí pasa).
 */
export function createRental(payload: {
  client_id?: string;
  client_name?: string;
  assets: Array<{ asset_id?: string; asset_name?: string; qty?: number; rate_amount?: number | null; rate_unit?: string }>;
  rate_unit?: string;
  start_date?: string;
  end_date?: string;
  deposit?: number | null;
  subtotal?: number | null;
  total?: number | null;
  has_pagare?: boolean;
  pagare_number?: string;
  notes?: string;
  status?: string;
}): Promise<Rental> {
  return req<Rental>(`${BASE}/rentals`, { method: 'POST', body: JSON.stringify(payload) });
}

export function listRentals(params: { status?: string; client_id?: string; limit?: number } = {}): Promise<Rental[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.client_id) qs.set('client_id', params.client_id);
  qs.set('limit', String(params.limit ?? 200));
  return req<Rental[]>(`${BASE}/rentals?${qs.toString()}`);
}

export function rentalsDue(days = 3): Promise<Rental[]> {
  return req<Rental[]>(`${BASE}/rentals/due?days=${days}`);
}

export function debtors(): Promise<Rental[]> {
  return req<Rental[]>(`${BASE}/rentals/debtors`);
}

export function getRental(id: string): Promise<Rental> {
  return req<Rental>(`${BASE}/rentals/${id}`);
}

export function swapAsset(rentalId: string, body: {
  old_asset_id: string; new_asset_id: string; reason?: string;
}): Promise<Rental> {
  return req<Rental>(`${BASE}/rentals/${rentalId}/swap`, { method: 'POST', body: JSON.stringify(body) });
}

/**
 * Devolución de equipos. Sin `asset_ids` regresa TODA la renta.
 *
 * `hour_meters` ({asset_id: horas}) es la pieza que cierra F1+F3: la lectura
 * se toma justo cuando la máquina vuelve de obra, se acumula y puede disparar
 * el servicio automático. Es opcional para no bloquear devoluciones donde no
 * se alcanzó a tomar.
 */
export function returnRental(rentalId: string, body: { asset_ids?: string[]; note?: string; close?: boolean; hour_meters?: Record<string, number> } = {}): Promise<Rental> {
  return req<Rental>(`${BASE}/rentals/${rentalId}/return`, { method: 'POST', body: JSON.stringify(body) });
}

/** Tablero del dueño: utilización, ingreso, disponibilidad por categoría, conversión y cobranza. */
export function fleetBI(params: { start_date?: string; end_date?: string } = {}): Promise<FleetBI> {
  const qs = new URLSearchParams();
  if (params.start_date) qs.set('start_date', params.start_date);
  if (params.end_date) qs.set('end_date', params.end_date);
  const q = qs.toString();
  return req<FleetBI>(`${BASE}/bi/fleet${q ? `?${q}` : ''}`);
}

/** Utilización y ROI POR UNIDAD: qué fierro produce y cuál estorba en el patio. */
export function fleetUnits(params: { start_date?: string; end_date?: string; category?: string } = {}): Promise<UnitBI[]> {
  const qs = new URLSearchParams();
  if (params.start_date) qs.set('start_date', params.start_date);
  if (params.end_date) qs.set('end_date', params.end_date);
  if (params.category) qs.set('category', params.category);
  const q = qs.toString();
  return req<UnitBI[]>(`${BASE}/bi/units${q ? `?${q}` : ''}`);
}

/**
 * Registra la lectura del horómetro (típicamente al regreso de obra).
 *
 * Las 74 unidades estan en 0 h, asi que el ciclo de servicio automatico de F3
 * nunca dispara: todas dicen "faltan 200 h" aunque lleven anios trabajando.
 * Capturar la lectura real es lo que lo pone en marcha.
 */
export function recordHourMeter(assetId: string, body: { hours: number; note?: string }): Promise<Asset> {
  return req<Asset>(`${BASE}/assets/${assetId}/hour-meter`, { method: 'POST', body: JSON.stringify(body) });
}

/**
 * ¿Cuántas unidades hay LIBRES en esas fechas? (RentalOS F6)
 *
 * La pregunta del vendedor ANTES de prometer maquinaria — es lo que evita la
 * sobreventa. Cuenta solo lo rentable: lo que está en servicio o reparación
 * sale del calendario.
 */
export function checkAvailability(body: {
  start_date: string;
  end_date: string;
  category?: string;
  name?: string;
}): Promise<Availability> {
  return req<Availability>(`${BASE}/availability`, { method: 'POST', body: JSON.stringify(body) });
}
